/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Custom Change Claim Work State button
 */

import { Button, App, ButtonProps } from 'antd';
import { CLAIM_WORK_STATES } from 'app-constants/claims/claimRequest';
import { MODAL_TYPES } from '../modal';
import useUpdateClaimById from 'hooks/claim/mutate/useUpdateClaimById';

export interface IProps extends ButtonProps {
	workState: ClaimRequest.IClaimWorkState;
	previousWorkState: ClaimRequest.IClaimWorkState;
	claimId: string;
	claimResource: Fhir.Claim;
}

const getClaimWorkStateModalContent = (isClosedClaim: boolean): string | React.ReactNode => {
	if (!isClosedClaim) {
		return (
			<>
				{"'Closed' claims will appear in the 'Inactive' tab of all applicable worklists and the 'Ongoing Claims' view."}
				<br />
				{'You can reopen this claim at any time. '}
			</>
		);
	}
	return "Once reopened, this claim will return to the 'Active' tab of all applicable worklists and the 'Ongoing Claims' view.";
};

const ChangeClaimWorkState: React.FC<IProps> = ({ workState, previousWorkState, claimId, claimResource, ...restProps }) => {
	const { mutateAsync } = useUpdateClaimById(claimId);
	const { modal } = App.useApp();

	const isClosedClaim = workState === CLAIM_WORK_STATES.ia_closed.value;

	const changeClaimWorkState = () => {
		modal.confirm({
			...MODAL_TYPES.warning,
			width: 560,
			title: `Are you sure you want to ${isClosedClaim ? 'reopen' : 'close'} this claim?`,
			content: getClaimWorkStateModalContent(isClosedClaim),
			okText: isClosedClaim ? 'Yes, reopen' : 'Yes, close',
			cancelText: 'Cancel',
			onOk: async () => {
				await mutateAsync({
					claimResource,
					workState: isClosedClaim ? previousWorkState : CLAIM_WORK_STATES.ia_closed.value,
				});
			},
		});
	};

	return (
		<Button {...restProps} danger={!isClosedClaim} onClick={changeClaimWorkState}>
			{isClosedClaim ? 'Re-open Claim' : 'Close Claim'}
		</Button>
	);
};

export default ChangeClaimWorkState;
